import React, { useState } from 'react';
import { SmartDataTable } from '@src/components/SmartDataTable';
import { usePatientsColumns, usePatientCardView } from './hooks';
import { usePatientFormFields, CreateForm } from '@src/components/shared/forms';
import { useAppointmentsFormFields } from '../appointments/hooks';
import { useResource, useInvalidate } from '@refinedev/core';
import { Button, Switch, Space } from 'antd';
import { CalendarOutlined, TableOutlined, AppstoreOutlined, MenuOutlined } from '@ant-design/icons';
import { useNavigation } from '@refinedev/core';

export const PatientsList = () => {
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [createAppointmentFormVisible, setCreateAppointmentFormVisible] =
    useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const { push } = useNavigation();
  const invalidate = useInvalidate();

  const columns = usePatientsColumns({ showCreatedAt: true }); // You can set this to false to hide the column
  const formFields = usePatientFormFields();
  const appointmentFormFields = useAppointmentsFormFields();
  const { resource } = useResource();
  const { PatientCardView } = usePatientCardView();

  const handleViewPatient = (record: any) => {
    // Open patient details in new tab
    const route = resource?.route || '/patients';
    const url = `${window.location.origin}${route}/${record.id}`;
    window.open(url, '_blank');
  };

  // Function to handle create appointment
  const handleCreateAppointment = () => {
    setCreateAppointmentFormVisible(true);
  };

  // Function to handle appointment creation success
  const handleAppointmentCreated = (data: any) => {
    setCreateAppointmentFormVisible(false);

    // Invalidate appointments cache to refresh the data
    invalidate({
      resource: 'appointments',
      invalidates: ['list', 'many'],
    });

    // Navigate to appointments page to see the new appointment
    push('/appointments');
  };

  // Create custom title component with Create Appointment button
  const createCustomTitle = () => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>Edit Patient</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button
            type="default"
            icon={<CalendarOutlined />}
            size="small"
            onClick={handleCreateAppointment}
          >
            Create Appointment
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-hidden">

      <SmartDataTable
        api={{
          resource: 'patients',
        }}
        title="Patients"
        columns={columns}
        search={{
          enabled: true,
          fields: ['id', 'first_name', 'last_name', 'mobile_no', 'email'],
          placeholder: 'Search by ID, name, mobile, or email...',
          debounceMs: 500,
        }}
        createForm={{
          fields: formFields as any,
          title: 'Create Patient',
          submitText: 'Create Patient',
        }}
        editForm={{
          fields: formFields as any,
          title: 'Edit Patient',
          submitText: 'Save Changes',
          customTitle: createCustomTitle(),
        }}
        formDisplayMode="drawer"
        drawerWidth={1200}
        showCreate={true}
        showEdit={true}
        showDelete={true}
        showView={true}
        onView={handleViewPatient}
        pageSize={10}
        showSizeChanger={true}
        showQuickJumper={false}
        // showExport={true}
        showColumnVisibility={true}
        tableProps={{
          scroll: {
            x: 'max-content',
            y: 'calc(100vh - 240px)',
          },
        }}
        rowKey="id"
        // Chat configuration
        enableChat={true}
        chatType="PATIENT"
        chatTitle="Patient Messages"
        chatPlaceholder="Add a note for patient..."
        isintialScroll={true}
        // Handle edit click to capture selected record
        onEditClick={record => {
          setSelectedRecord(record);
        }}
        // Use PatientCardView as mobile card component
        mobileCardComponent={PatientCardView}
      />

      {/* Appointment Creation Form */}
      <CreateForm
        resource="appointments"
        fields={appointmentFormFields as any}
        title="Create New Appointment"
        submitText="Create Appointment"
        open={createAppointmentFormVisible}
        onClose={() => setCreateAppointmentFormVisible(false)}
        onSuccess={handleAppointmentCreated}
        formDisplayMode="modal"
        modalWidth={1200}
        initialValues={
          selectedRecord
            ? {
                patient_id: selectedRecord.id,
                patient_name:
                  `${selectedRecord.first_name || ''} ${selectedRecord.last_name || ''}`.trim(),
                patient_mobile_no: selectedRecord.mobile_no.substring(2),
              }
            : {}
        }
      />
    </div>
  );
};
